import { type FormEvent, useDeferredValue, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  Cpu,
  Globe2,
  Handshake,
  LoaderCircle,
  Megaphone,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  WalletCards,
  Workflow,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AdminApp } from "@/AdminApp";
import {
  getRiskLevelLabel,
  getVerticalLabel,
  leadRiskLevelOptions,
  leadVerticalOptions,
  parseLeadSubmission,
  type LeadRiskLevel,
  type LeadSubmissionInput,
  type LeadSubmissionReceipt,
  type LeadVertical,
} from "@/lib/lead-submission";

import "./index.css";

const marketStats = [
  { value: "128", label: "在架能力", note: "按任务而不是按 logo 编目" },
  { value: "32", label: "撮合中线索", note: "本周新增 9 个意向单" },
  { value: "11", label: "付费置顶商家", note: "平台先做 B2B lead gen" },
  { value: "6 天", label: "平均接入周期", note: "从 BD 对接到沙箱验证" },
];

const sponsorPackages = [
  { title: "白名单入驻", price: "免费", detail: "完成能力卡、联系人和基础 SLA 填写" },
  { title: "认证商家", price: "¥29,800 / 年", detail: "加上合规标签、标准话术和优先推荐" },
  { title: "场景置顶", price: "¥120,000 / 季", detail: "按任务场景抢首页和搜索结果第一屏" },
];

const demandBoard = [
  { team: "Kimi 生态", need: "找可做酒店比价 + 预订单的旅行能力", stage: "本周约 3 家" },
  { team: "智谱应用平台", need: "找企业客服工单写入和 CRM 同步能力", stage: "正在筛供应商" },
  { team: "某 AI 采购助手", need: "找发票校验、报销流转、支付确认链路", stage: "要求沙箱可测" },
];

const capabilityCatalog = [
  {
    company: "飞猪开放平台",
    vertical: "travel" as const,
    buyerType: "model",
    capability: "酒店库存、价格日历、预订单草稿",
    summary: "适合旅行 Agent 做比价、推荐和下单前确认。",
    pricing: "CPS + 特价活动位",
    auth: "OAuth + IP 白名单",
    sla: "99.9%",
    tags: ["酒店", "比价", "预订单"],
    sponsored: true,
    readiness: "支持沙箱联调",
  },
  {
    company: "去哪儿供应链",
    vertical: "travel" as const,
    buyerType: "model",
    capability: "机票搜索、退改签规则、价格波动提醒",
    summary: "适合大模型做航班查询、价格对比和行程建议。",
    pricing: "按出票分佣",
    auth: "API Key",
    sla: "99.5%",
    tags: ["机票", "航班", "价格提醒"],
    sponsored: false,
    readiness: "已开放文档",
  },
  {
    company: "途家商旅",
    vertical: "travel" as const,
    buyerType: "app",
    capability: "民宿库存、房型图文、清洁费计算",
    summary: "长尾住宿供给丰富，适合差异化住宿推荐。",
    pricing: "按成交抽成",
    auth: "OAuth",
    sla: "99.7%",
    tags: ["民宿", "房型", "商旅"],
    sponsored: true,
    readiness: "支持当日沙箱账号",
  },
  {
    company: "有赞云",
    vertical: "commerce" as const,
    buyerType: "app",
    capability: "商品目录、库存查询、优惠券校验",
    summary: "适合电商导购 Agent 做库存问答和促销组合。",
    pricing: "按调用量阶梯计费",
    auth: "OAuth + Scope",
    sla: "99.9%",
    tags: ["电商", "库存", "优惠券"],
    sponsored: false,
    readiness: "支持标准 OpenAPI",
  },
  {
    company: "微盟门店",
    vertical: "commerce" as const,
    buyerType: "model",
    capability: "门店预约、核销码生成、到店状态回传",
    summary: "适合本地生活和预约型 Agent 做闭环交易前置。",
    pricing: "按门店数 + 订单提成",
    auth: "JWT + 审批流",
    sla: "99.5%",
    tags: ["预约", "核销", "门店"],
    sponsored: false,
    readiness: "需人工审批写权限",
  },
  {
    company: "Udesk 客服云",
    vertical: "saas" as const,
    buyerType: "app",
    capability: "创建工单、同步会话、查客户画像",
    summary: "适合企业服务 Agent 处理客服、售后和分单。",
    pricing: "年费 + 席位数",
    auth: "OAuth + 审计日志",
    sla: "99.95%",
    tags: ["客服", "工单", "CRM"],
    sponsored: true,
    readiness: "支持细粒度权限",
  },
  {
    company: "金蝶云星空",
    vertical: "saas" as const,
    buyerType: "model",
    capability: "发票识别、报销单创建、付款状态查询",
    summary: "适合中小企业财务 Agent 做流程自动化。",
    pricing: "企业订阅 + 调用包",
    auth: "企业网关签名",
    sla: "99.9%",
    tags: ["财税", "发票", "报销"],
    sponsored: false,
    readiness: "需要企业管理员授权",
  },
  {
    company: "纷享销客",
    vertical: "saas" as const,
    buyerType: "app",
    capability: "线索录入、商机更新、回访任务生成",
    summary: "适合销售 Agent 做 CRM 写入和线索路由。",
    pricing: "按企业版授权",
    auth: "OAuth + 审批节点",
    sla: "99.8%",
    tags: ["CRM", "销售", "线索"],
    sponsored: false,
    readiness: "支持测试租户",
  },
];

const pipeline = [
  { title: "能力卡上架", detail: "把商家能力拆成可搜索、可比较、可约谈的标准卡片。" },
  { title: "场景匹配", detail: "按任务场景和接入要求做首轮筛选，不让 BD 从零开始找。" },
  { title: "沙箱验证", detail: "先跑查询或草稿写入，再决定是否进入正式商务合作。" },
  { title: "交易深化", detail: "后续再叠加认证、分账、计费和更深的运行时能力。" },
];

const trustPoints = [
  {
    icon: ShieldCheck,
    title: "不是黄页",
    detail: "每个商家都按鉴权方式、可写权限、SLA 和场景适配度结构化展示。",
  },
  {
    icon: Target,
    title: "先做撮合",
    detail: "MVP 先解决让双方快速碰面，不一开始背全链路交易的复杂度。",
  },
  {
    icon: Workflow,
    title: "可继续做厚",
    detail: "一旦撮合有效，就可以往认证、沙箱、结算和运行时路由继续堆壁垒。",
  },
  {
    icon: Megaphone,
    title: "商家先买单",
    detail: "短期最现实的收入来自置顶、认证和线索服务，不是向模型厂商收费。",
  },
];

const buyerOptions = [
  { value: "all", label: "全部买方" },
  { value: "model", label: "基座模型 / 平台方" },
  { value: "app", label: "AI 应用 / Agent 团队" },
];

const initialLeadForm: LeadSubmissionInput = {
  name: "旅行规划 Agent",
  vertical: "travel",
  riskLevel: "query",
  notes: "需要酒店比价、房型描述、预订单草稿能力。优先支持 OAuth、沙箱和佣金模式。",
  contactName: "Joe",
  contactHandle: "joe@example.com / 微信号",
  website: "",
};

type LeadApiResponse =
  | {
      ok: true;
      lead?: LeadSubmissionReceipt;
      ignored?: boolean;
    }
  | {
      ok: false;
      error: string;
    };

function formatLeadTime(createdAt: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(createdAt));
}

export function App() {
  const pathname = typeof window === "undefined" ? "/" : window.location.pathname.replace(/\/+$/, "") || "/";

  if (pathname === "/admin") {
    return <AdminApp />;
  }

  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());
  const [vertical, setVertical] = useState<LeadVertical | "all">("all");
  const [buyerType, setBuyerType] = useState("all");
  const [leadForm, setLeadForm] = useState<LeadSubmissionInput>(initialLeadForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitReceipt, setSubmitReceipt] = useState<LeadSubmissionReceipt | null>(null);

  const filteredCapabilities = capabilityCatalog.filter(item => {
    const matchesVertical = vertical === "all" || item.vertical === vertical;
    const matchesBuyer = buyerType === "all" || item.buyerType === buyerType;
    const haystack = `${item.company} ${item.capability} ${item.summary} ${item.tags.join(" ")}`.toLowerCase();
    const matchesQuery = deferredQuery === "" || haystack.includes(deferredQuery);

    return matchesVertical && matchesBuyer && matchesQuery;
  });

  function updateLeadForm<K extends keyof LeadSubmissionInput>(field: K, value: LeadSubmissionInput[K]) {
    setLeadForm(current => ({ ...current, [field]: value }));
  }

  async function handleLeadSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setSubmitError("");

    const parsed = parseLeadSubmission(leadForm);

    if (!parsed.ok) {
      setSubmitReceipt(null);
      setSubmitError(parsed.error);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(parsed.data),
      });

      const payload = (await response.json()) as LeadApiResponse;

      if (!response.ok || !payload.ok || !payload.lead) {
        setSubmitReceipt(null);
        setSubmitError(payload.ok ? "提交失败，请稍后再试。" : payload.error);
        return;
      }

      setSubmitReceipt(payload.lead);
      setSubmitError("");
    } catch (error) {
      console.error("Failed to submit lead", error);
      setSubmitReceipt(null);
      setSubmitError("网络异常，稍后再试。");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="relative min-h-screen overflow-hidden">
      <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col gap-10 px-5 py-6 sm:px-8 lg:px-10 lg:py-8">
        <header className="flex flex-col gap-4 rounded-full border border-white/50 bg-white/70 px-5 py-4 shadow-[0_20px_60px_rgba(64,46,24,0.08)] backdrop-blur md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="grid size-11 place-items-center rounded-full bg-[#1f5147] text-white shadow-lg shadow-[#1f5147]/20">
              <Handshake className="size-5" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-[#7b5c39]">LLMAD DEMO</p>
              <p className="text-sm text-slate-600">Agent 能力招商台</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <span className="rounded-full bg-[#f9f2e8] px-3 py-1">供给侧招商</span>
            <span className="rounded-full bg-[#eef6f3] px-3 py-1">需求侧免费使用</span>
            <span className="rounded-full bg-[#f2f4fb] px-3 py-1">先撮合后做厚</span>
          </div>
        </header>

        <section className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_360px]">
          <div className="rounded-[2rem] border border-white/60 bg-white/76 p-7 shadow-[0_30px_80px_rgba(42,33,18,0.12)] backdrop-blur xl:p-9">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#ddc7a8] bg-[#fff7ed] px-3 py-1 text-sm text-[#8a5b1f]">
              <Sparkles className="size-4" />
              不是做 API 黄页，而是做 AI 时代的渠道入口
            </div>
            <div className="max-w-4xl space-y-5">
              <h1 className="font-display text-4xl leading-tight text-slate-950 sm:text-5xl xl:text-6xl">
                让商家把自己的能力，
                <br />
                直接卖进大模型和 Agent 的入口。
              </h1>
              <p className="max-w-3xl text-base leading-8 text-slate-600 sm:text-lg">
                这版 demo 先做最小闭环：能力目录、需求发布、置顶商家、人工撮合和状态面板。现在表单已经接到后端，
                真实需求会写进 Cloudflare D1，方便你后面直接看线索。
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="rounded-full bg-[#1f5147] px-6 text-white hover:bg-[#173d36]">
                发布 AI 需求
                <ArrowRight className="size-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-[#d7c0a1] bg-white/70 px-6 text-[#6e5534] hover:bg-[#fff7ed]"
              >
                申请场景置顶
              </Button>
            </div>

            <div className="mt-9 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {marketStats.map(item => (
                <Card key={item.label} className="border-white/60 bg-[#fffdf9]/90 py-5 shadow-sm">
                  <CardContent className="space-y-2 px-5">
                    <p className="text-3xl font-semibold text-slate-950">{item.value}</p>
                    <div>
                      <p className="text-sm font-medium text-slate-700">{item.label}</p>
                      <p className="text-sm leading-6 text-slate-500">{item.note}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="space-y-5">
            <Card className="overflow-hidden border-[#f0dcc2] bg-[#fff7ed]/88 py-0 shadow-[0_24px_48px_rgba(130,87,34,0.12)]">
              <CardHeader className="gap-3 border-b border-[#ecd7ba] py-5">
                <CardTitle className="flex items-center gap-2 text-lg text-[#724718]">
                  <Megaphone className="size-4" />
                  商家付费位
                </CardTitle>
                <CardDescription className="text-[#8b6c49]">
                  前期先卖置顶、认证和线索，别把自己当成流量广告平台。
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 py-5">
                {sponsorPackages.map(item => (
                  <div key={item.title} className="rounded-2xl border border-[#ead4b6] bg-white/80 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium text-slate-900">{item.title}</p>
                        <p className="mt-1 text-sm leading-6 text-slate-600">{item.detail}</p>
                      </div>
                      <span className="shrink-0 rounded-full bg-[#1f5147] px-3 py-1 text-xs font-medium text-white">
                        {item.price}
                      </span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-white/60 bg-[#f4fbf8]/88 shadow-[0_22px_48px_rgba(36,94,84,0.14)]">
              <CardHeader className="gap-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Cpu className="size-4 text-[#1f5147]" />
                  当前热门需求
                </CardTitle>
                <CardDescription>让商家看到需求，才能形成抢位和出价意愿。</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {demandBoard.map(item => (
                  <div key={item.team} className="rounded-2xl border border-[#d7e7e2] bg-white/88 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium text-slate-900">{item.team}</p>
                      <span className="rounded-full bg-[#eef6f3] px-3 py-1 text-xs text-[#1f5147]">{item.stage}</span>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{item.need}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <Card className="border-white/60 bg-white/78 shadow-[0_24px_70px_rgba(47,35,18,0.1)]">
            <CardHeader className="gap-3">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <CardTitle className="text-2xl text-slate-950">按任务找能力，不按公司看 logo</CardTitle>
                  <CardDescription className="mt-2 max-w-2xl leading-7">
                    平台真正的最小单元是 capability。你要先验证大家是不是在这里找能力，而不是验证全链路交易系统。
                  </CardDescription>
                </div>
                <div className="rounded-full bg-[#f3f4f6] px-4 py-2 text-sm text-slate-600">
                  当前命中 <span className="font-semibold text-slate-950">{filteredCapabilities.length}</span> 个能力
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-3 rounded-[1.5rem] border border-slate-200/70 bg-[#fcfbf7] p-4 md:grid-cols-[minmax(0,1fr)_220px_220px]">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">关键词</label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={query}
                      onChange={event => setQuery(event.target.value)}
                      placeholder="搜酒店、CRM、报销、库存、预订单"
                      className="h-11 rounded-full border-slate-200 bg-white pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">场景</label>
                  <Select value={vertical} onValueChange={value => setVertical(value as LeadVertical | "all")}>
                    <SelectTrigger className="h-11 w-full rounded-full border-slate-200 bg-white px-4">
                      <SelectValue placeholder="选场景" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">全部场景</SelectItem>
                      {leadVerticalOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">买方类型</label>
                  <Select value={buyerType} onValueChange={setBuyerType}>
                    <SelectTrigger className="h-11 w-full rounded-full border-slate-200 bg-white px-4">
                      <SelectValue placeholder="选买方" />
                    </SelectTrigger>
                    <SelectContent>
                      {buyerOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                {filteredCapabilities.map(item => (
                  <Card key={`${item.company}-${item.capability}`} className="border-slate-200/80 bg-[#fffefb] py-0 shadow-sm">
                    <CardHeader className="gap-4 border-b border-slate-100 py-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-[#eef6f3] px-3 py-1 text-xs font-medium text-[#1f5147]">
                              {getVerticalLabel(item.vertical)}
                            </span>
                            {item.sponsored && (
                              <span className="rounded-full bg-[#fff0d8] px-3 py-1 text-xs font-medium text-[#8a5b1f]">
                                置顶赞助
                              </span>
                            )}
                          </div>
                          <CardTitle className="text-xl text-slate-950">{item.company}</CardTitle>
                          <CardDescription className="text-sm leading-7 text-slate-600">{item.summary}</CardDescription>
                        </div>
                        <Building2 className="size-6 shrink-0 text-slate-300" />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-5 py-5">
                      <div>
                        <p className="text-sm font-medium text-slate-500">能力范围</p>
                        <p className="mt-2 text-base leading-7 text-slate-900">{item.capability}</p>
                      </div>

                      <div className="grid gap-3 text-sm sm:grid-cols-3">
                        <div className="rounded-2xl bg-[#f8f7f1] p-3">
                          <p className="text-slate-500">计费</p>
                          <p className="mt-1 font-medium text-slate-900">{item.pricing}</p>
                        </div>
                        <div className="rounded-2xl bg-[#f8f7f1] p-3">
                          <p className="text-slate-500">鉴权</p>
                          <p className="mt-1 font-medium text-slate-900">{item.auth}</p>
                        </div>
                        <div className="rounded-2xl bg-[#f8f7f1] p-3">
                          <p className="text-slate-500">SLA</p>
                          <p className="mt-1 font-medium text-slate-900">{item.sla}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {item.tags.map(tag => (
                          <span key={tag} className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600">
                            {tag}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center justify-between gap-4 rounded-2xl border border-[#d7e7e2] bg-[#f4fbf8] px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-[#1f5147]">
                          <BadgeCheck className="size-4" />
                          {item.readiness}
                        </div>
                        <Button className="rounded-full bg-[#1f5147] px-4 text-white hover:bg-[#173d36]">
                          发起撮合
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {filteredCapabilities.length === 0 && (
                <div className="rounded-[1.75rem] border border-dashed border-slate-300 bg-[#fbfaf6] px-6 py-10 text-center text-slate-500">
                  当前筛选条件下没有结果。这个空态本身也能用来验证需求侧是否会留下新需求。
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-5">
            <Card className="border-white/60 bg-[#f6fbff]/82 shadow-[0_22px_52px_rgba(41,73,106,0.1)]">
              <CardHeader className="gap-3">
                <CardTitle className="text-lg text-slate-950">为什么这版 demo 值得拿去聊</CardTitle>
                <CardDescription>先证明有人愿意来找、有人愿意上架、有人愿意买位。</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {trustPoints.map(point => {
                  const Icon = point.icon;
                  return (
                    <div key={point.title} className="rounded-2xl border border-[#dde8f3] bg-white/90 p-4">
                      <div className="flex items-center gap-3">
                        <div className="grid size-10 place-items-center rounded-full bg-[#edf4fa] text-[#355e7d]">
                          <Icon className="size-4" />
                        </div>
                        <p className="font-medium text-slate-900">{point.title}</p>
                      </div>
                      <p className="mt-3 text-sm leading-6 text-slate-600">{point.detail}</p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card className="border-white/60 bg-[#fffdf8]/84 shadow-[0_20px_46px_rgba(80,60,27,0.1)]">
              <CardHeader className="gap-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <WalletCards className="size-4 text-[#8a5b1f]" />
                  先赚什么钱
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm leading-7 text-slate-600">
                <p>1. 认证费：帮商家把能力卡、场景话术和接入要求整理好。</p>
                <p>2. 置顶费：按场景买首页和搜索结果第一屏。</p>
                <p>3. 线索费：撮合成功后按有效商务机会收费。</p>
                <p>4. 后续再谈分账、结算和更深的运行时能力。</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
          <Card className="border-white/60 bg-white/80 shadow-[0_24px_70px_rgba(45,33,18,0.1)]">
            <CardHeader className="gap-3">
              <CardTitle className="text-2xl text-slate-950">需求发布台</CardTitle>
              <CardDescription className="max-w-2xl leading-7">
                跟模型厂商或 AI 应用团队聊的时候，不要问感不感兴趣，直接让对方按这个模板把真实需求写出来。
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="grid gap-4" onSubmit={handleLeadSubmit}>
                <input
                  type="text"
                  name="website"
                  autoComplete="off"
                  tabIndex={-1}
                  className="hidden"
                  value={leadForm.website}
                  onChange={event => updateLeadForm("website", event.target.value)}
                />

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">需求名称</label>
                    <Input
                      value={leadForm.name}
                      onChange={event => updateLeadForm("name", event.target.value)}
                      className="h-11 rounded-2xl border-slate-200 bg-[#fdfcf8]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">主要场景</label>
                    <Select value={leadForm.vertical} onValueChange={value => updateLeadForm("vertical", value as LeadVertical)}>
                      <SelectTrigger className="h-11 w-full rounded-2xl border-slate-200 bg-[#fdfcf8]">
                        <SelectValue placeholder="选场景" />
                      </SelectTrigger>
                      <SelectContent>
                        {leadVerticalOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">风险等级</label>
                    <Select value={leadForm.riskLevel} onValueChange={value => updateLeadForm("riskLevel", value as LeadRiskLevel)}>
                      <SelectTrigger className="h-11 w-full rounded-2xl border-slate-200 bg-[#fdfcf8]">
                        <SelectValue placeholder="选风险" />
                      </SelectTrigger>
                      <SelectContent>
                        {leadRiskLevelOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">推荐买方</label>
                    <Input value="Kimi / 智谱 / 企业 Agent 团队" readOnly className="h-11 rounded-2xl border-slate-200 bg-[#f1f6f4]" />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">联系人</label>
                    <Input
                      value={leadForm.contactName}
                      onChange={event => updateLeadForm("contactName", event.target.value)}
                      placeholder="怎么称呼你"
                      className="h-11 rounded-2xl border-slate-200 bg-[#fdfcf8]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">联系方式</label>
                    <Input
                      value={leadForm.contactHandle}
                      onChange={event => updateLeadForm("contactHandle", event.target.value)}
                      placeholder="微信、邮箱或手机号"
                      className="h-11 rounded-2xl border-slate-200 bg-[#fdfcf8]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">需求说明</label>
                  <Textarea
                    value={leadForm.notes}
                    onChange={event => updateLeadForm("notes", event.target.value)}
                    className="min-h-28 rounded-3xl border-slate-200 bg-[#fdfcf8]"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="rounded-full bg-[#1f5147] px-6 text-white hover:bg-[#173d36] disabled:bg-[#6a8f87]"
                  >
                    {isSubmitting ? (
                      <>
                        <LoaderCircle className="size-4 animate-spin" />
                        提交中
                      </>
                    ) : (
                      <>
                        提交撮合需求
                        <ArrowRight className="size-4" />
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-slate-500">这一步已经接了真实后端，提交后会写入 Cloudflare D1。</p>
                </div>

                {submitError && (
                  <div className="rounded-2xl border border-[#f0c3bf] bg-[#fff5f4] px-4 py-3 text-sm text-[#9a433c]">
                    {submitError}
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          <Card className="border-white/60 bg-[#f3fbf7]/84 shadow-[0_24px_54px_rgba(29,79,63,0.12)]">
            <CardHeader className="gap-3">
              <CardTitle className="text-lg text-slate-950">撮合状态面板</CardTitle>
              <CardDescription>把“平台不是只放链接”这件事讲清楚。</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {submitReceipt ? (
                <div className="rounded-[1.5rem] border border-[#cde2db] bg-white/92 p-5">
                  <div className="flex items-center gap-2 text-[#1f5147]">
                    <CheckCircle2 className="size-5" />
                    <span className="font-medium">需求已进入撮合池</span>
                  </div>
                  <div className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
                    <p>
                      <span className="font-medium text-slate-900">名称：</span>
                      {submitReceipt.name}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">场景：</span>
                      {getVerticalLabel(submitReceipt.vertical)}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">风险：</span>
                      {getRiskLevelLabel(submitReceipt.riskLevel)}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">联系人：</span>
                      {submitReceipt.contactName} / {submitReceipt.contactHandle}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">提交时间：</span>
                      {formatLeadTime(submitReceipt.createdAt)}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">线索编号：</span>
                      {submitReceipt.id}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">下一步：</span>
                      平台推荐 3 家供应商，48 小时内安排首轮联调。
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-[1.5rem] border border-dashed border-[#cfe0d8] bg-white/86 p-5 text-sm leading-7 text-slate-500">
                  提交一个需求后，这里会显示线索编号、提交时间和下一步动作。
                </div>
              )}

              <div className="space-y-3">
                {pipeline.map(item => (
                  <div key={item.title} className="rounded-2xl border border-[#d3e6de] bg-white/92 p-4">
                    <div className="flex items-center gap-3">
                      <div className="grid size-9 place-items-center rounded-full bg-[#e9f5f0] text-[#1f5147]">
                        <Globe2 className="size-4" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{item.title}</p>
                        <p className="text-sm leading-6 text-slate-600">{item.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </main>
  );
}

export default App;
